import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from '@/components/ui/button';
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Play, Loader2 } from 'lucide-react';
import { Company } from '@/helpers/scrapeDatabase';

interface RunScrapeModalsProps {
  // Single scrape modal
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  selectedSlug: string;
  setSelectedSlug: (slug: string) => void;
  companies: Company[];
  forceFlag: boolean;
  setForceFlag: (force: boolean) => void;
  useAIFlag: boolean;
  setUseAIFlag: (useAI: boolean) => void;
  isRunning: boolean;
  onRunScrape: () => void;

  // All pages modal
  isAllPagesModalOpen: boolean;
  setIsAllPagesModalOpen: (open: boolean) => void;
  isRunningAllPages: boolean;
  onRunAllPagesScrapes: () => void;

  // Category modal
  isCategoryModalOpen: boolean;
  setIsCategoryModalOpen: (open: boolean) => void;
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  categories: string[];
  isRunningCategoryPages: boolean;
  onRunCategoryScrapes: () => void;
}

export const RunScrapeModals: React.FC<RunScrapeModalsProps> = ({
  isModalOpen,
  setIsModalOpen,
  selectedSlug,
  setSelectedSlug,
  companies,
  forceFlag,
  setForceFlag,
  useAIFlag,
  setUseAIFlag,
  isRunning,
  onRunScrape,
  isAllPagesModalOpen,
  setIsAllPagesModalOpen,
  isRunningAllPages,
  onRunAllPagesScrapes,
  isCategoryModalOpen,
  setIsCategoryModalOpen,
  selectedCategory,
  setSelectedCategory,
  categories,
  isRunningCategoryPages,
  onRunCategoryScrapes,
}) => {
  return (
    <>
      {/* Single Scrape Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogTrigger asChild>
          <Button variant="default" size="sm">
            <Play className="h-4 w-4 mr-2" />
            Run Single Scrape
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Run Single Scrape</DialogTitle>
            <DialogDescription>
              Select a company to run a screenshot scrape for.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="company" className="text-right">
                Company
              </Label>
              <div className="col-span-3">
                <Select value={selectedSlug} onValueChange={setSelectedSlug}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((company) => (
                      <SelectItem key={company.slug} value={company.slug}>
                        {company.slug}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="force" className="text-right">
                Force
              </Label>
              <div className="col-span-3 flex items-center space-x-2">
                <Checkbox
                  id="force"
                  checked={forceFlag}
                  onCheckedChange={(checked) => setForceFlag(checked as boolean)}
                />
                <Label htmlFor="force" className="text-sm font-normal">
                  Force re-scrape even if recent data exists
                </Label>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="useAI" className="text-right">
                Use AI
              </Label>
              <div className="col-span-3 flex items-center space-x-2">
                <Checkbox
                  id="useAI"
                  checked={useAIFlag}
                  onCheckedChange={(checked) => setUseAIFlag(checked as boolean)}
                />
                <Label htmlFor="useAI" className="text-sm font-normal">
                  Use AI for processing
                </Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="submit"
              onClick={onRunScrape}
              disabled={isRunning || !selectedSlug}
              className="flex items-center gap-2"
            >
              {isRunning ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Run Scrape
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* All Pages Modal */}
      <Dialog open={isAllPagesModalOpen} onOpenChange={setIsAllPagesModalOpen}>
        <DialogTrigger asChild>
          <Button variant="secondary" size="sm">
            <Play className="h-4 w-4 mr-2" />
            Run All Pages
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Run All Pages Scrape</DialogTitle>
            <DialogDescription>
              This will run scrapes for all companies. This may take a while.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="force-all" className="text-right">
                Force
              </Label>
              <div className="col-span-3 flex items-center space-x-2">
                <Checkbox
                  id="force-all"
                  checked={forceFlag}
                  onCheckedChange={(checked) => setForceFlag(checked as boolean)}
                />
                <Label htmlFor="force-all" className="text-sm font-normal">
                  Force re-scrape even if recent data exists
                </Label>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="useAI-all" className="text-right">
                Use AI
              </Label>
              <div className="col-span-3 flex items-center space-x-2">
                <Checkbox
                  id="useAI-all"
                  checked={useAIFlag}
                  onCheckedChange={(checked) => setUseAIFlag(checked as boolean)}
                />
                <Label htmlFor="useAI-all" className="text-sm font-normal">
                  Use AI for processing
                </Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="submit"
              onClick={onRunAllPagesScrapes}
              disabled={isRunningAllPages}
              className="flex items-center gap-2"
            >
              {isRunningAllPages ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Run All Pages
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Modal */}
      <Dialog open={isCategoryModalOpen} onOpenChange={setIsCategoryModalOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm">
            <Play className="h-4 w-4 mr-2" />
            Run Category
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Run Category Scrape</DialogTitle>
            <DialogDescription>
              Select a category to run scrapes for all companies in that category.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="category" className="text-right">
                Category
              </Label>
              <div className="col-span-3">
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="force-category" className="text-right">
                Force
              </Label>
              <div className="col-span-3 flex items-center space-x-2">
                <Checkbox
                  id="force-category"
                  checked={forceFlag}
                  onCheckedChange={(checked) => setForceFlag(checked as boolean)}
                />
                <Label htmlFor="force-category" className="text-sm font-normal">
                  Force re-scrape even if recent data exists
                </Label>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="useAI-category" className="text-right">
                Use AI
              </Label>
              <div className="col-span-3 flex items-center space-x-2">
                <Checkbox
                  id="useAI-category"
                  checked={useAIFlag}
                  onCheckedChange={(checked) => setUseAIFlag(checked as boolean)}
                />
                <Label htmlFor="useAI-category" className="text-sm font-normal">
                  Use AI for processing
                </Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="submit"
              onClick={onRunCategoryScrapes}
              disabled={isRunningCategoryPages || !selectedCategory}
              className="flex items-center gap-2"
            >
              {isRunningCategoryPages ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Run Category
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
